import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft, Mic, Users, BookOpen, CheckCircle, Clock, 
  MessageSquare, Send, Star, Trophy, Target, Calendar,
  ChevronRight, Play, FileText, Video, Headphones,
  Award, TrendingUp, Eye, Edit2, Loader2, RefreshCw,
  GraduationCap, Sparkles, AlertCircle, CheckCircle2
} from "lucide-react";

// Podcast Academy Curriculum Structure
const CURRICULUM = [
  {
    week: 1,
    title: "Foundation & Vision",
    description: "Define your podcast concept, target audience, and unique value proposition",
    lessons: [
      { id: "1-1", title: "Finding Your Podcast Niche", type: "video", duration: "15 min" },
      { id: "1-2", title: "Defining Your Ideal Listener", type: "worksheet", duration: "30 min" },
      { id: "1-3", title: "Crafting Your Show Concept", type: "exercise", duration: "45 min" },
      { id: "1-4", title: "Week 1 Assignment", type: "assignment", duration: "1 hr" }
    ]
  },
  {
    week: 2,
    title: "Equipment & Tech Setup",
    description: "Set up your recording environment and choose the right equipment",
    lessons: [
      { id: "2-1", title: "Essential Podcasting Equipment", type: "video", duration: "20 min" },
      { id: "2-2", title: "Budget-Friendly Setup Options", type: "guide", duration: "15 min" },
      { id: "2-3", title: "Recording Software Walkthrough", type: "video", duration: "25 min" },
      { id: "2-4", title: "Setting Up Your Recording Space", type: "checklist", duration: "30 min" }
    ]
  },
  {
    week: 3,
    title: "Content Planning",
    description: "Create your content strategy and episode framework",
    lessons: [
      { id: "3-1", title: "Episode Format Options", type: "video", duration: "15 min" },
      { id: "3-2", title: "Content Calendar Creation", type: "worksheet", duration: "45 min" },
      { id: "3-3", title: "Episode Outline Template", type: "template", duration: "20 min" },
      { id: "3-4", title: "Planning Your First 10 Episodes", type: "assignment", duration: "1 hr" }
    ]
  },
  {
    week: 4,
    title: "Recording Mastery",
    description: "Master the art of recording professional-quality audio",
    lessons: [
      { id: "4-1", title: "Microphone Techniques", type: "video", duration: "15 min" },
      { id: "4-2", title: "Vocal Warm-ups & Delivery", type: "exercise", duration: "20 min" },
      { id: "4-3", title: "Remote Interview Best Practices", type: "guide", duration: "25 min" },
      { id: "4-4", title: "Record Your First Episode", type: "assignment", duration: "2 hr" }
    ]
  },
  {
    week: 5,
    title: "Editing & Production",
    description: "Learn to edit and produce polished episodes",
    lessons: [
      { id: "5-1", title: "Editing Fundamentals", type: "video", duration: "30 min" },
      { id: "5-2", title: "Adding Music & Sound Effects", type: "tutorial", duration: "20 min" },
      { id: "5-3", title: "Creating Your Intro/Outro", type: "exercise", duration: "45 min" },
      { id: "5-4", title: "Edit Your First Episode", type: "assignment", duration: "2 hr" }
    ]
  },
  {
    week: 6,
    title: "Branding & Artwork",
    description: "Create compelling podcast branding and cover art",
    lessons: [
      { id: "6-1", title: "Podcast Branding Essentials", type: "video", duration: "15 min" },
      { id: "6-2", title: "Cover Art Best Practices", type: "guide", duration: "20 min" },
      { id: "6-3", title: "Creating Show Notes Templates", type: "template", duration: "30 min" },
      { id: "6-4", title: "Design Your Podcast Brand", type: "assignment", duration: "1.5 hr" }
    ]
  },
  {
    week: 7,
    title: "Distribution & Hosting",
    description: "Get your podcast on all major platforms",
    lessons: [
      { id: "7-1", title: "Choosing a Podcast Host", type: "video", duration: "15 min" },
      { id: "7-2", title: "Submitting to Apple Podcasts", type: "walkthrough", duration: "20 min" },
      { id: "7-3", title: "Spotify & Other Platforms", type: "guide", duration: "25 min" },
      { id: "7-4", title: "Launch Your Podcast", type: "assignment", duration: "1 hr" }
    ]
  },
  {
    week: 8,
    title: "Launch Strategy",
    description: "Plan and execute a successful podcast launch",
    lessons: [
      { id: "8-1", title: "Pre-Launch Checklist", type: "checklist", duration: "15 min" },
      { id: "8-2", title: "Building Launch Buzz", type: "video", duration: "20 min" },
      { id: "8-3", title: "Launch Day Strategy", type: "guide", duration: "25 min" },
      { id: "8-4", title: "Execute Your Launch Plan", type: "assignment", duration: "2 hr" }
    ]
  },
  {
    week: 9,
    title: "Growth & Marketing",
    description: "Grow your audience with proven marketing strategies",
    lessons: [
      { id: "9-1", title: "Social Media for Podcasters", type: "video", duration: "20 min" },
      { id: "9-2", title: "SEO for Podcasts", type: "guide", duration: "15 min" },
      { id: "9-3", title: "Cross-Promotion Strategies", type: "video", duration: "20 min" },
      { id: "9-4", title: "Create Your Growth Plan", type: "assignment", duration: "1 hr" }
    ]
  },
  {
    week: 10,
    title: "Guest Booking",
    description: "Learn to book and interview amazing guests",
    lessons: [
      { id: "10-1", title: "Finding & Pitching Guests", type: "video", duration: "20 min" },
      { id: "10-2", title: "Guest Outreach Templates", type: "template", duration: "15 min" },
      { id: "10-3", title: "Interview Techniques", type: "video", duration: "25 min" },
      { id: "10-4", title: "Book Your First Guest", type: "assignment", duration: "1 hr" }
    ]
  },
  {
    week: 11,
    title: "Monetization",
    description: "Turn your podcast into a revenue stream",
    lessons: [
      { id: "11-1", title: "Monetization Models Overview", type: "video", duration: "20 min" },
      { id: "11-2", title: "Sponsorship & Ads", type: "guide", duration: "25 min" },
      { id: "11-3", title: "Creating Premium Content", type: "video", duration: "20 min" },
      { id: "11-4", title: "Build Your Monetization Plan", type: "assignment", duration: "1.5 hr" }
    ]
  },
  {
    week: 12,
    title: "Sustainability & Scale",
    description: "Build systems for long-term podcasting success",
    lessons: [
      { id: "12-1", title: "Batch Recording Systems", type: "video", duration: "15 min" },
      { id: "12-2", title: "Outsourcing & Team Building", type: "guide", duration: "20 min" },
      { id: "12-3", title: "Analytics & Optimization", type: "video", duration: "20 min" },
      { id: "12-4", title: "Final Project: Launch Review", type: "assignment", duration: "2 hr" }
    ]
  }
];

const lessonTypeIcons = {
  video: Video,
  worksheet: FileText,
  exercise: Target,
  assignment: CheckCircle,
  guide: BookOpen,
  template: FileText,
  checklist: CheckCircle2,
  walkthrough: Play,
  tutorial: Headphones
};

const lessonTypeColors = {
  video: "bg-red-100 text-red-600",
  worksheet: "bg-blue-100 text-blue-600",
  exercise: "bg-green-100 text-green-600",
  assignment: "bg-purple-100 text-purple-600",
  guide: "bg-amber-100 text-amber-600",
  template: "bg-cyan-100 text-cyan-600",
  checklist: "bg-emerald-100 text-emerald-600",
  walkthrough: "bg-pink-100 text-pink-600",
  tutorial: "bg-indigo-100 text-indigo-600"
};

export const PodcastAcademyAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ week: "", lesson: "", feedback: "", rating: 5 });
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async (showToast = false) => {
    if (showToast) setRefreshing(true);
    try {
      const res = await authFetch(`${API}/consultant/podcast-academy/enrollments`);
      if (res.ok) {
        const data = await res.json();
        setEnrollments(data);
        if (showToast) toast.success("Data refreshed");
      }
    } catch (_error) {
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const sendFeedback = async () => {
    if (!selectedClient || !feedbackForm.feedback) return;
    
    setSendingFeedback(true);
    try {
      const res = await authFetch(`${API}/consultant/podcast-academy/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: selectedClient.user_id,
          week: parseInt(feedbackForm.week) || selectedClient.current_week,
          lesson_id: feedbackForm.lesson,
          feedback: feedbackForm.feedback,
          rating: feedbackForm.rating
        })
      });

      if (res.ok) {
        toast.success("Feedback sent to client!");
        setShowFeedbackDialog(false);
        setFeedbackForm({ week: "", lesson: "", feedback: "", rating: 5 });
        fetchEnrollments();
      } else {
        toast.error("Failed to send feedback");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSendingFeedback(false);
    }
  };

  const updateClientProgress = async (clientId, week, status) => {
    try {
      const res = await authFetch(`${API}/consultant/podcast-academy/progress`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, week, status })
      });

      if (res.ok) {
        toast.success("Progress updated!");
        fetchEnrollments();
      }
    } catch (_error) {
      toast.error("Failed to update progress");
    }
  };

  const getProgressPercentage = (enrollment) => {
    const currentWeek = enrollment.current_week || 1;
    const completedLessons = enrollment.completed_lessons?.length || 0;
    const totalLessons = CURRICULUM.reduce((acc, week) => acc + week.lessons.length, 0);
    return Math.round((completedLessons / totalLessons) * 100);
  };

  const getClientInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "??";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="podcast-academy-admin">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/consultant")}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center">
                  <Mic className="w-6 h-6 mr-2" />
                  Podcast Academy
                </h1>
                <p className="text-purple-100 text-sm">Training Management Dashboard</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchEnrollments(true)}
              disabled={refreshing}
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-purple-500" />
                <Badge className="bg-purple-100 text-purple-700">Active</Badge>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{enrollments.length}</p>
              <p className="text-sm text-neutral-500">Enrolled Students</p>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <Badge className="bg-green-100 text-green-700">Progress</Badge>
              </div>
              <p className="text-3xl font-bold text-neutral-900">
                {enrollments.length > 0 
                  ? Math.round(enrollments.reduce((acc, e) => acc + getProgressPercentage(e), 0) / enrollments.length)
                  : 0}%
              </p>
              <p className="text-sm text-neutral-500">Avg Completion</p>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <BookOpen className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-3xl font-bold text-neutral-900">12</p>
              <p className="text-sm text-neutral-500">Week Program</p>
            </CardContent>
          </Card>

          <Card className="border-pink-200 bg-gradient-to-br from-pink-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Award className="w-5 h-5 text-pink-500" />
                <Badge className="bg-pink-100 text-pink-700">Graduates</Badge>
              </div>
              <p className="text-3xl font-bold text-neutral-900">
                {enrollments.filter(e => e.status === "completed").length}
              </p>
              <p className="text-sm text-neutral-500">Completed</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-50">
              <Users className="w-4 h-4 mr-2" />
              Student Overview
            </TabsTrigger>
            <TabsTrigger value="curriculum" className="data-[state=active]:bg-purple-50">
              <BookOpen className="w-4 h-4 mr-2" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-purple-50">
              <TrendingUp className="w-4 h-4 mr-2" />
              Progress Tracker
            </TabsTrigger>
          </TabsList>

          {/* Student Overview Tab */}
          <TabsContent value="overview">
            {enrollments.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="p-12 text-center">
                  <GraduationCap className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Students Enrolled</h3>
                  <p className="text-neutral-500 mb-6">Students will appear here once they enroll in the Podcast Academy program.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enrollments.map((enrollment) => (
                  <Card key={enrollment.user_id} className="border-neutral-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500">
                            <AvatarFallback className="text-white font-semibold">
                              {getClientInitials(enrollment.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-neutral-900">{enrollment.name}</p>
                            <p className="text-sm text-neutral-500">{enrollment.email}</p>
                          </div>
                        </div>
                        <Badge className={
                          enrollment.status === "completed" ? "bg-green-100 text-green-700" :
                          enrollment.status === "active" ? "bg-blue-100 text-blue-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {enrollment.status || "Active"}
                        </Badge>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-neutral-600">Progress</span>
                            <span className="font-medium">{getProgressPercentage(enrollment)}%</span>
                          </div>
                          <Progress value={getProgressPercentage(enrollment)} className="h-2" />
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-600">Current Week</span>
                          <span className="font-medium text-purple-600">
                            Week {enrollment.current_week || 1} of 12
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-600">Lessons Completed</span>
                          <span className="font-medium">
                            {enrollment.completed_lessons?.length || 0} / {CURRICULUM.reduce((acc, w) => acc + w.lessons.length, 0)}
                          </span>
                        </div>

                        {enrollment.last_activity && (
                          <div className="flex items-center text-xs text-neutral-500">
                            <Clock className="w-3 h-3 mr-1" />
                            Last active: {new Date(enrollment.last_activity).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setSelectedClient(enrollment);
                            setActiveTab("progress");
                          }}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View Progress
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                          onClick={() => {
                            setSelectedClient(enrollment);
                            setShowFeedbackDialog(true);
                          }}
                        >
                          <MessageSquare className="w-3 h-3 mr-1" />
                          Feedback
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Curriculum Tab */}
          <TabsContent value="curriculum">
            <div className="space-y-4">
              {CURRICULUM.map((week) => (
                <Card key={week.week} className="border-neutral-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {week.week}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{week.title}</CardTitle>
                          <CardDescription>{week.description}</CardDescription>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-purple-600 border-purple-200">
                        {week.lessons.length} lessons
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {week.lessons.map((lesson) => {
                        const Icon = lessonTypeIcons[lesson.type] || FileText;
                        const colorClass = lessonTypeColors[lesson.type] || "bg-neutral-100 text-neutral-600";
                        return (
                          <LessonCard
                            key={lesson.id}
                            lesson={lesson}
                            icon={Icon}
                            colorClass={colorClass}
                            weekTitle={week.title}
                          />
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Progress Tracker Tab */}
          <TabsContent value="progress">
            <Card className="border-neutral-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Student Progress Tracker</CardTitle>
                    <CardDescription>Track and update individual student progress through the curriculum</CardDescription>
                  </div>
                  {selectedClient && (
                    <Select
                      value={selectedClient?.user_id}
                      onValueChange={(value) => {
                        const client = enrollments.find(e => e.user_id === value);
                        setSelectedClient(client);
                      }}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Select student" />
                      </SelectTrigger>
                      <SelectContent>
                        {enrollments.map((e) => (
                          <SelectItem key={e.user_id} value={e.user_id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedClient && enrollments.length > 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500 mb-4">Select a student to view their detailed progress</p>
                    <Select
                      onValueChange={(value) => {
                        const client = enrollments.find(e => e.user_id === value);
                        setSelectedClient(client);
                      }}
                    >
                      <SelectTrigger className="w-64 mx-auto">
                        <SelectValue placeholder="Select student" />
                      </SelectTrigger>
                      <SelectContent>
                        {enrollments.map((e) => (
                          <SelectItem key={e.user_id} value={e.user_id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : selectedClient ? (
                  <div className="space-y-4">
                    {/* Selected Client Header */}
                    <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                      <Avatar className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500">
                        <AvatarFallback className="text-white font-semibold text-lg">
                          {getClientInitials(selectedClient.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-neutral-900">{selectedClient.name}</p>
                        <p className="text-sm text-neutral-500">{selectedClient.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-600">{getProgressPercentage(selectedClient)}%</p>
                        <p className="text-sm text-neutral-500">Complete</p>
                      </div>
                    </div>

                    {/* Week-by-Week Progress */}
                    <div className="space-y-3">
                      {CURRICULUM.map((week) => {
                        const weekCompleted = selectedClient.completed_weeks?.includes(week.week);
                        const isCurrentWeek = selectedClient.current_week === week.week;
                        const completedLessons = week.lessons.filter(
                          l => selectedClient.completed_lessons?.includes(l.id)
                        ).length;

                        return (
                          <div 
                            key={week.week}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              weekCompleted 
                                ? 'bg-green-50 border-green-200' 
                                : isCurrentWeek 
                                  ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200' 
                                  : 'bg-white border-neutral-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                  weekCompleted 
                                    ? 'bg-green-500 text-white' 
                                    : isCurrentWeek 
                                      ? 'bg-purple-500 text-white' 
                                      : 'bg-neutral-200 text-neutral-600'
                                }`}>
                                  {weekCompleted ? <CheckCircle className="w-5 h-5" /> : week.week}
                                </div>
                                <div>
                                  <p className="font-medium text-neutral-900">{week.title}</p>
                                  <p className="text-sm text-neutral-500">
                                    {completedLessons} / {week.lessons.length} lessons completed
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isCurrentWeek && (
                                  <Badge className="bg-purple-100 text-purple-700">Current</Badge>
                                )}
                                {!weekCompleted && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => updateClientProgress(selectedClient.user_id, week.week, "completed")}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Mark Complete
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <GraduationCap className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">No students enrolled yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-purple-500" />
              Send Feedback to {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Week</label>
                <Select
                  value={feedbackForm.week}
                  onValueChange={(value) => setFeedbackForm({ ...feedbackForm, week: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select week" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRICULUM.map((week) => (
                      <SelectItem key={week.week} value={week.week.toString()}>
                        Week {week.week}: {week.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Rating</label>
                <Select
                  value={feedbackForm.rating.toString()}
                  onValueChange={(value) => setFeedbackForm({ ...feedbackForm, rating: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((r) => (
                      <SelectItem key={r} value={r.toString()}>
                        {r} Star{r !== 1 ? 's' : ''} {"⭐".repeat(r)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Feedback Message</label>
              <Textarea
                value={feedbackForm.feedback}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback: e.target.value })}
                placeholder="Write your feedback, encouragement, or suggestions..."
                rows={5}
              />
            </div>
            <Button
              onClick={sendFeedback}
              disabled={sendingFeedback || !feedbackForm.feedback}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {sendingFeedback ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PodcastAcademyAdmin;

const LESSON_GUIDANCE = {
  "video": "Watch this video lesson carefully and take notes on key concepts. Pause and reflect on how each point applies to your own podcast journey.",
  "worksheet": "Complete this worksheet step by step. Don't rush — thoughtful answers here become the foundation for your podcast strategy.",
  "exercise": "This hands-on exercise helps you practice what you've learned. Set aside uninterrupted time and work through each part.",
  "assignment": "This is your main deliverable for the week. Apply everything you've learned to create something tangible for your podcast.",
  "guide": "Read through this guide completely before starting. Bookmark sections you'll want to reference later.",
  "template": "Use this template as your starting framework. Customize it to match your brand voice and audience.",
  "checklist": "Work through each item on the checklist in order. Check off items as you complete them — every box matters.",
  "walkthrough": "Follow along with this step-by-step walkthrough. Have the platform/tool open so you can practice in real-time.",
  "tutorial": "This tutorial builds skills progressively. Complete each section before moving to the next."
};

const LessonCard = ({ lesson, icon: Icon, colorClass, weekTitle }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div
      className={`rounded-lg transition-all cursor-pointer ${
        expanded ? "bg-white border-2 border-purple-200 shadow-sm" : "bg-neutral-50 hover:bg-neutral-100 border border-transparent"
      }`}
      onClick={() => setExpanded(!expanded)}
      data-testid={`lesson-card-${lesson.id}`}
    >
      <div className="flex items-center gap-3 p-3">
        <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-900">{lesson.title}</p>
          <p className="text-xs text-neutral-500">{lesson.type} &bull; {lesson.duration}</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-neutral-400 transition-transform flex-shrink-0 ${expanded ? "rotate-90" : ""}`} />
      </div>
      
      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-neutral-100 mt-0">
          <div className="bg-purple-50 rounded-lg p-3 mt-2">
            <p className="text-xs font-medium text-purple-700 mb-1">Guidance for Consultant</p>
            <p className="text-xs text-neutral-700 leading-relaxed">
              {LESSON_GUIDANCE[lesson.type] || "Guide your client through this lesson. Check in with them after completion to discuss any questions."}
            </p>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
            <Clock className="w-3 h-3" />
            <span>Estimated time: {lesson.duration}</span>
            <span className="text-neutral-300">|</span>
            <span>Week: {weekTitle}</span>
          </div>
        </div>
      )}
    </div>
  );
};
